//lib/theme-context.tsx
'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { themes, Theme } from './themes';

interface ThemeContextProps {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextProps>({
  theme: 'default',
  setTheme: () => {},
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>('default');

  useEffect(() => {
    const stored = localStorage.getItem('theme') as Theme | null;
    const initial = stored && themes.includes(stored) ? stored : 'default';
    applyTheme(initial);
  }, []);

  const applyTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.remove(...themes);
    if (newTheme !== 'default') {
      document.documentElement.classList.add(newTheme); // 'default' is :root, no class needed
    }
  };

  const setTheme = (newTheme: Theme) => applyTheme(newTheme);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
